import { useState, useEffect, type ChangeEvent } from "react";
import Modal from "@modalSrc/Modal";
import RightTimeApi from "@/API";
import '@/style/sass/_efDetailsModal.scss';

type EFResponse = {
    formId?: number;
    field?: string;
    value?: string;
};

type EFStatus = {
    ctsId?: number;
    display?: string;
    description?: string;
};

type EFDetails = {
    testId?: number;
    statusId?: number;
    statuses?: EFStatus[];
    responses?: EFResponse[];
};

type EFTableData = {
    Cultures?: string[];
    Behaviors?: string[];
    PI?: string[];
};

type EFDetailsModalProps = {
    testId?: number | string;
    onClose?: () => void;
    title?: string;
};

export default function EFDetailsModal(props: EFDetailsModalProps) {
    const { testId, onClose, title } = props;
    const [efDetails, setEfDetails] = useState<EFDetails | null>(null);
    const [efData, setEfData] = useState<EFTableData | null>(null);

    const onSetStatus = async (e: ChangeEvent<HTMLSelectElement>) => {
        // Update the status in the backend
        const data = {
            testId: efDetails?.testId,
            newStatusId: parseInt(e.target.value)
        };
        const response = await RightTimeApi.putAsync(`TestStatus`, data);
        if (response && response.ok) {
            if (onClose) onClose();
        }
    };

    useEffect(() => {
        if (testId) {
            // Fetch BE details based on testId

            fetchEfDetails();
        }
    }, [testId]);

    if (!testId) {
        return null;
    }

    const fetchEfDetails = async () => {
        const response = await RightTimeApi.getAsync(`EFDetails/${testId}`);
        if (response && response.ok) {
            setEfDetails(response.jsonData);
            const values: EFTableData = {};
            const swap: Record<number, string | string[]> = {
                1501: 'Innovation',
                1502: 'Focus',
                1503: 'Task Emphasis',
                1504: 'Cooperation',
                1505: 'Work Atmosphere',
                1506: 'Goals',
                1101: 'Attendance',
                1102: 'Change Tolerance',
                1103: 'Comprehends Instructions',
                1104: 'Dependability',
                1105: 'Detail Orientation',
                1106: 'Follows Directions',
                1107: 'Pride In Work',
                1108: 'Quality Minded',
                1109: 'Respect For Others',
                1110: 'Response To Criticism',
                1111: 'Response To Leadership',
                1112: 'Self Discipline',
                1113: 'Team Work',
                1114: 'GoalOriented',
                1801: ["Inventive", "Curious", "Cautious"],
                1802: ["Efficient", "Spontaneous", "Easy-going"],
                1803: ["Outgoing", "Independent", "Reserved"],
                1804: ["Friendly", "Compassionate", "Assertive"]
            };
            const responses = (response.jsonData.responses || []) as EFResponse[];
            values['Cultures'] = responses.filter((r) => r.formId === 1500 && r.field !== '1500')
                .map((r) =>
                    (`${String(swap[parseInt(r.field || "0", 10)] || r.field)}-${parseInt(r.value || "0", 10)}`)
                );

            values['Behaviors'] = responses.filter((r) => r.formId === 1100 && parseInt(r.field || "0", 10) > 1100 && parseInt(r.field || "0", 10) < 1120)
                .map((r) =>
                    String(swap[parseInt(r.field || "0", 10)] || r.value || "")
                );
            const customResponses = responses.filter((r) => r.formId === 1100 && parseInt(r.field || "0", 10) >= 1120);
            if (customResponses.length > 0) {
                values['Behaviors'].push('');
                customResponses.forEach((r) => {
                    values['Behaviors'].push(r.value || "");
                });
            }

            values['PI'] = responses.filter((r) => r.formId === 1800 && parseInt(r.field || "0", 10) > 1800)
                .map((r) => {
                    const fieldArray = swap[parseInt(r.field || "0", 10)];
                    if (fieldArray && Array.isArray(fieldArray)) {
                        const index = parseInt(r.value || "0", 10) - 1;
                        return fieldArray[index] || r.value || "";
                    }
                    return r.value || "";
                });

            setEfData(values);
        }
    };

    const footer = efDetails && efDetails.statuses && (
        <>
            <div className="footerLeft">
                {/* Left side footer content can be added here */}
            </div>
            <div className="footerRight">
                <label htmlFor="statusSelect">Status:</label>
                <select id="statusSelect" onChange={onSetStatus} value={efDetails.statuses?.some(s => s.ctsId === efDetails.statusId) ? efDetails.statusId : ""}>
                    <option value="">Select an option</option>
                    {efDetails && efDetails.statuses && efDetails.statuses.map((status, index) => (
                        <option key={index} value={status.ctsId}>{`${status.display || ""} - ${status.description || ""}`}</option>
                    ))}
                </select>
                <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={onClose}
                >
                    Close
                </button>
            </div>
        </>
    );

    return (
        <Modal
            isOpen={!!testId}
            title={title}
            onClose={onClose}
            footer={footer}
            className="efDetailsModal"
        >
            <table>
                <thead>
                    <tr>
                        <th>Cultures</th>
                        <th>Behaviors</th>
                        <th>PI</th>
                    </tr>
                </thead>
                <tbody>
                    {(() => {
                        if (!efData) return null;

                        // Find the maximum length among all arrays
                        const maxLength = Math.max(
                            efData.Cultures?.length || 0,
                            efData.Behaviors?.length || 0,
                            efData.PI?.length || 0
                        );

                        // Create rows for each index
                        const rows = [];
                        for (let i = 0; i < maxLength; i++) {
                            rows.push(
                                <tr key={i}>
                                    <td>{efData.Cultures?.[i] || ''}</td>
                                    <td>{efData.Behaviors?.[i] || ''}</td>
                                    <td>{efData.PI?.[i] || ''}</td>
                                </tr>
                            );
                        }
                        return rows;
                    })()}
                </tbody>
            </table>

        </Modal>
    );
}

